import React, { useMemo } from 'react';
import { Box, CircularProgress, Skeleton, Typography } from '@mui/material';
import ArtifactFrame from './ArtifactFrame';
import ArtifactAppFrame from './ArtifactAppFrame';

export const ARTIFACT_RENDERERS = {
  SAFE_HTML: 'safe_html',
  SANDBOXED_JS_APP: 'sandboxed_js_app',
};

function artifactContextFrom(artifact) {
  return {
    artifactInstanceId: artifact?.artifactInstanceId || '',
    artifactDefinitionId: artifact?.artifactDefinitionId || '',
    artifactType: artifact?.artifactType || '',
    title: artifact?.title || '',
    description: artifact?.description || '',
    allowedEvents: artifact?.allowedEvents || [],
    runtimeManifest: artifact?.runtimeManifest || {},
  };
}

function ArtifactAppPlaceholder({ className, style, minHeight, message, loading }) {
  return (
    <Box
      className={className}
      style={style}
      role={loading ? 'status' : 'alert'}
      aria-live="polite"
      sx={{
        alignItems: 'center',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: loading ? 'divider' : 'error.light',
        borderRadius: 2,
        color: loading ? 'text.secondary' : 'error.main',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        justifyContent: 'center',
        minHeight,
        p: 3,
        textAlign: 'center',
      }}
    >
      {loading ? (
        <>
          <CircularProgress size={28} thickness={4} />
          <Box sx={{ maxWidth: 360, width: '100%' }}>
            <Skeleton variant="text" width="75%" sx={{ mx: 'auto' }} />
            <Skeleton variant="text" width="52%" sx={{ mx: 'auto' }} />
          </Box>
        </>
      ) : null}
      <Typography variant="body2" sx={{ fontWeight: loading ? 500 : 600 }}>
        {message}
      </Typography>
    </Box>
  );
}

export default function ArtifactRenderer({
  artifact,
  html,
  compiledJs,
  renderer,
  disabled = false,
  onSubmitEvent,
  className,
  title = 'Artifact',
  minHeight = 240,
  style,
}) {
  const resolvedRenderer =
    renderer || artifact?.renderer || ARTIFACT_RENDERERS.SAFE_HTML;
  const artifactContext = useMemo(() => artifactContextFrom(artifact), [artifact]);

  if (resolvedRenderer === ARTIFACT_RENDERERS.SANDBOXED_JS_APP) {
    const bundle = compiledJs ?? artifact?.compiledJs ?? '';
    if (!bundle) {
      const message = artifact?.previewMessage || 'Artifact app bundle unavailable.';
      const loading =
        artifact?.previewLoading ||
        message === 'Compiling TypeScript preview...' ||
        message === 'TypeScript preview will compile after you stop editing.';
      return (
        <ArtifactAppPlaceholder
          className={className}
          style={style}
          minHeight={minHeight}
          message={message}
          loading={loading}
        />
      );
    }
    return (
      <ArtifactAppFrame
        compiledJs={bundle}
        artifactContext={artifactContext}
        disabled={disabled}
        onSubmitEvent={onSubmitEvent}
        className={className}
        title={title}
        minHeight={minHeight}
        style={style}
      />
    );
  }

  return (
    <ArtifactFrame
      html={html ?? artifact?.sanitizedHtml ?? artifact?.html ?? ''}
      disabled={disabled}
      onSubmitEvent={onSubmitEvent}
      className={className}
      title={title}
      minHeight={minHeight}
      style={style}
    />
  );
}
